<template>
  <div v-if="loading">
    <RemarkSkeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
  </div>
  <div v-else-if="contextId && eligibleRemarks.length === 0">
    <div
      class="
        tw-text-base tw-font-medium tw-text-sf-gray-dark tw-p-2 tw-text-center
      "
    >
      <span class="tw-text-sm">{{ messageForContext }}</span>
    </div>
  </div>
  <template v-for="item in items" v-else :key="item.id">
    <component
      :is="item.size"
      v-if="isTitle(item)"
      :class="['tw-pt-2', titleClass(item)]"
    >
      {{ item.content }}
    </component>

    <div
      v-else-if="isRemark(item) && isEligibleRemark(item)"
      class="tw-flex tw-flex-col tw-gap-4"
    >
      <RemarkCard
        :remark="item"
        part-of="meeting_preparation"
        :show-context="false"
        slim
        :ordinal="ordinal(item)"
        :presentation-comments="presentationComments || []"
        show-period
        hide-read-status
        show-tags
      />
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import RemarkCard from '@app/shared/vue/components/molecules/remark/remark-card.vue'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import type {
  Remark,
  Title,
  RemarkContextType,
} from '@app/application/communication/types'

import {
  isRemark,
  isTitle,
} from '@app/application/client_meetings/screens/prepare/utils'

import useI18n from '@app/shared/vue/composables/use-I18n'

interface Props {
  items: (Remark | Title)[]
  presentationComments: string[]
  contextId?: number
  contextType?: RemarkContextType
  ledgerId?: number
  loading: boolean
  // Clamp title headings for the narrow sidebar; unset keeps full size.
  compact?: boolean
}

const props = defineProps<Props>()

const I18n = useI18n()

const COMPACT_TITLE_CLASSES: Record<string, string> = {
  h1: 'tw-text-xl',
  h2: 'tw-text-lg',
  h3: 'tw-text-base',
}

const titleClass = (item: Title): string =>
  props.compact ? COMPACT_TITLE_CLASSES[item.size] ?? '' : ''

const eligibleRemarks = computed(() =>
  props.items.filter(isRemark).filter(isEligibleRemark)
)

const ordinal = (remark: Remark) => {
  const eligible = eligibleRemarks.value
  return eligible.findIndex((r) => r.id === remark.id) + 1
}

function isEligibleRemark(remark: Remark) {
  if (!props.contextId) {
    return true
  }

  if (remark.todo && remark.done) {
    return false
  }

  if (
    props.contextType === remark.contextType &&
    remark.contextId === props.contextId
  ) {
    if (remark.ledgerId === props.ledgerId) {
      return true
    }

    if (remark.ledgerId !== props.ledgerId && remark.sticky) {
      return true
    }
  }

  return false
}

const messageForContext = computed(() => {
  if (!props.contextType) {
    return ''
  }

  return I18n.t(
    `detailed_entry.sidebar.no_messages_${props.contextType
      .toLowerCase()
      .replace('::', '_')}`
  )
})
</script>
